import { useMatchRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import {
  Box,
  Link,
  Typography,
  Alert as MuiAlert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Verified } from "@mui/icons-material";
import useResendVerification from "@/hooks/useResendVerification";
import CustomSnackbar from "../snakcbars/CustomSnackBar";
import { useSession } from "@/security/RouteGuards";
import { FamousPlatformButtonType } from "../buttons/FamousPlatformButton";
import { RequireProperty, SessionType } from "@/types/index";
import VerifyEmailExceedingBandwidthModal from "../modals/VerifyEmailExceedingBandwidthModal";
import { DEFAULT_CUSTOMIZE_PATHNAME } from "@/utils/formHref";
import { PAGE_MAX_WIDTH } from "@/providers/DrawerProvider";

const resolveOpenEmailClientDetails = (
  email: string
): [FamousPlatformButtonType?, string?] => {
  if (!email) return [undefined, undefined];

  const providers: {
    [key in FamousPlatformButtonType]?: { domains: string[]; link: string };
  } = {
    gmail: {
      domains: ["@gmail.com", "@googlemail.com"],
      link: `https://mail.google.com/mail/u/${email}/#search/from%3A(support%40webshare.io)+in%3Aanywhere`,
    },
    yahoo: {
      domains: ["@yahoo.com", "@myyahoo.com"],
      link: "https://mail.yahoo.com/d/search/keyword=from%253Asupport%40webshare.io",
    },
    outlook: {
      domains: ["@outlook.com", "@hotmail.com", "@live.com", "@msn.com"],
      link: `https://outlook.live.com/mail/?login_hint=${encodeURIComponent(
        email
      )}`,
    },
    "apple-mail": {
      domains: ["@icloud.com", "@mac.com", "@me.com"],
      link: "https://www.icloud.com/mail/",
    },
  };

  for (let providerKey in providers) {
    const provider = providerKey as FamousPlatformButtonType;
    if (providers[provider]?.domains.some((domain) => email.includes(domain))) {
      return [provider, providers[provider]?.link || ""];
    }
  }

  return [undefined, undefined];
};

export default function VerifyEmailBanner() {
  const matchRoute = useMatchRoute();

  const { profile, activationStatus } = useSession() as RequireProperty<
    SessionType,
    "profile"
  >;
  const [openEmailPlatform, openEmailLink] = useMemo(
    () => resolveOpenEmailClientDetails(profile.email),
    [profile]
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    errorMessage,
    setErrorMessage,
    isRefreshSuccess,
    setIsRefreshSuccess,
    resendActivationEmail,
  } = useResendVerification();

  if (!activationStatus) return null;
  if (activationStatus.email_is_verified) return null;

  if (matchRoute({ to: DEFAULT_CUSTOMIZE_PATHNAME, fuzzy: true })) return null;

  return (
    <>
      <VerifyEmailExceedingBandwidthModal
        openEmailLink={openEmailLink}
        resendAction={resendActivationEmail}
      />
      <Box
        sx={{
          bgcolor: "background.paper",
          borderTop: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <CustomSnackbar
          isError
          snackbarOpen={!!errorMessage}
          setSnackbarOpen={() => {
            setErrorMessage(null);
          }}
          message={errorMessage ?? ""}
        />
        <CustomSnackbar
          snackbarOpen={isRefreshSuccess}
          setSnackbarOpen={() => {
            setIsRefreshSuccess(false);
          }}
          message="Verification email sent. Check your inbox."
        />
        <MuiAlert
          icon={<Verified fontSize={"small"} sx={{ color: "warning.dark" }} />}
          sx={{
            bgcolor: "transparent",
            color: "text.primary",
            borderRadius: 0,
            py: 0,
            px: isMobile ? 3 : 5,
            width: "100%",
            maxWidth: PAGE_MAX_WIDTH,
            mx: "auto",
            "& .MuiAlert-message": {
              display: "flex",
              alignItems: "center",
              py: 1,
            },
            "& .MuiAlert-icon": {
              alignItems: "center",
            },
          }}
        >
          <Typography variant="body2" component="span">
            {openEmailPlatform ? (
              <Link
                color="textPrimary"
                href={openEmailLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="verifyBtn"
                sx={{ fontWeight: 600, cursor: "pointer" }}
              >
                Verify
              </Link>
            ) : (
              <Typography display="inline" variant="body2">
                Verify
              </Typography>
            )}
            &nbsp;your email to ensure your bandwidth isn&apos;t limited —&nbsp;
            <Link
              component="button"
              color="textPrimary"
              onClick={resendActivationEmail}
              sx={{
                fontWeight: 600,
                cursor: "pointer",
                verticalAlign: "baseline",
              }}
            >
              Resend email
            </Link>
          </Typography>
        </MuiAlert>
      </Box>
    </>
  );
}
